/**
 * The title screen (R35, amended D116/D117, re-dressed D132): the pack's
 * hero, the pack's typed status, the two-step gate in the pack's words —
 * and, once awake, the MUTE toggle (Mike's D117 ask), so a player who wakes
 * the terminal somewhere quiet can silence the theme without entering the
 * session to find the control.
 *
 * @remarks
 * Extracted from `app-component.tsx` at D132, when every word and picture on
 * it became the pack's: a screen that renders from a manifest wants to be
 * rendered against several manifests, and the shell's boot spine only ever
 * loads one. Waking is the user gesture browsers demand before any sound;
 * the theme loops under the title for as long as the player lingers, and
 * only an awake terminal offers BEGIN. The mute drives the SAME preference
 * the in-session toggle drives (one source of truth — the session's driver
 * re-asserts it on entry), plus the player directly so the suspend lands now
 * rather than on the next store-driven render.
 *
 * @packageDocumentation
 */

import type { GameDescription } from '@orpheus/api-contracts';

import {
	ambientAudioPreferenceSet,
	autoWaitPreferenceSet,
	type GameStore,
	restartSession,
	selectAutoWaitPreference,
	selectIsAmbientAudioEnabled,
	selectSessionView,
} from '@orpheus/game-store';
import {
	AmbientAudioToggleComponent,
	ClockToggleComponent,
	ConfirmDialogComponent,
	RevealedTextComponent,
} from '@orpheus/player-ui';
import { type ReactNode, useMemo, useState, useSyncExternalStore } from 'react';

import { type AmbiencePlayer } from './ambience-player.js';
import { EngineCreditComponent } from './masthead-component.js';
import { isClockEnabledFor } from './use-pacing-driver.js';

/** `{count}` in a pack's line, as the number of locations walked — the MAP control's own count (D179). */
const withCount =
	(count: number) =>
		(template: string): string =>
			template.replaceAll('{count}', String(count));

/** Props for {@link TitleScreenSection}. */
export interface TitleScreenSectionProps {
	/** The shell's shared ambience player. */
	readonly ambiencePlayer: AmbiencePlayer;
	/** Whether the wake step has happened. */
	readonly hasWokenTerminal: boolean;
	/** The game's masthead, layered over the hero. */
	readonly masthead: ReactNode;
	/** Enter the session (the pack's begin label). */
	readonly onBegin: () => void;
	/** Wake the terminal (the audio gesture; starts the theme). */
	readonly onWake: () => void;
	/** The game's description — the title screen's copy and assets (D132), its clock (D153), already resolved (D156). */
	readonly game: GameDescription;
	/** The booted session's store — the mute preference's one source of truth. */
	readonly store: GameStore;
}

/**
 * Render the title screen.
 *
 * @param props - {@link TitleScreenSectionProps}
 * @returns The title screen
 */
export const TitleScreenSection = ({
	ambiencePlayer,
	game,
	hasWokenTerminal,
	masthead,
	onBegin,
	onWake,
	store,
}: TitleScreenSectionProps) => {
	const { clock, titleScreen } = game;
	const storeBinding = useMemo(
		() => ({
			getState: () => store.getState(),
			subscribe: (onStoreChange: () => void) => store.subscribe(onStoreChange),
		}),
		[store],
	);
	const storeState = useSyncExternalStore(storeBinding.subscribe, storeBinding.getState);
	const isAmbientAudioEnabled = selectIsAmbientAudioEnabled(storeState);
	const isClockEnabled = isClockEnabledFor(clock, selectAutoWaitPreference(storeState));
	// D179: the title tells the truth. The session the boot opened is the
	// resumed save when there is one; a turn taken means a descent is under
	// way, and the player is offered both doors rather than a BEGIN that
	// silently resumes. A save at turn zero is a fresh start by any measure.
	const view = selectSessionView(storeState);
	const isUnderWay = view !== undefined && view.turnCount > 0;
	const walked = withCount(view?.map.visitedCount ?? 0);
	const [isConfirmingRestart, setIsConfirmingRestart] = useState(false);
	const status = isUnderWay ? walked(titleScreen.status.inProgress) : titleScreen.status.awake;

	return (
		<section className="player-shell__title">
			{/*
			 * The full-bleed hero: a fixed viewport overlay, so the image escapes
			 * the shell's 52rem column and covers edge to edge. The picture is
			 * chrome — empty alt, aria-hidden — because the status line and the
			 * wordmark carry everything it says; the scrim underneath the content
			 * keeps the type at contrast whatever the picture does. A pack that
			 * declares no hero gets no picture, and the scrim over the page color
			 * is simply the page color.
			 */}
			{titleScreen.hero === undefined ? undefined : (
				<img alt="" aria-hidden="true" className="player-shell__title-hero" src={titleScreen.hero} />
			)}
			<div className="player-shell__title-scrim" />
			<div className="player-shell__title-content">
				{masthead}
				{/*
				 * The status line TYPES in — the R27 reveal at the system teletype
				 * pace, because this is exactly what that machinery is for: a
				 * machine putting text on a display. The key remounts the reveal on
				 * the wake, so the new status types over again; the cursor is pure
				 * chrome (aria-hidden), blinking at the line's end, steady under
				 * reduced motion.
				 */}
				<p className="player-shell__title-status" key={hasWokenTerminal ? 'awake' : 'asleep'}>
					<RevealedTextComponent
						cadence="stepped"
						stepMs={24}
						text={hasWokenTerminal ? status : titleScreen.status.asleep}
					/>
					<span aria-hidden="true" className="player-shell__title-cursor" />
				</p>
				{hasWokenTerminal ? (
					<>
						{/* D179 (Mike's ruling, option 2): a descent under way gets both doors — resume, or begin again behind a confirm. */}
						<div className="player-shell__doors">
							<button className="player-shell__begin" onClick={onBegin} type="button">
								{isUnderWay ? titleScreen.resumeLabel : titleScreen.beginLabel}
							</button>
							{isUnderWay ? (
								<button
									className="player-shell__begin player-shell__begin--danger"
									onClick={() => {
										setIsConfirmingRestart(true);
									}}
									type="button"
								>
									{titleScreen.restartLabel}
								</button>
							) : undefined}
						</div>
						{isConfirmingRestart ? (
							<ConfirmDialogComponent
								keepLabel={titleScreen.restart.keepLabel}
								onKeep={() => {
									setIsConfirmingRestart(false);
								}}
								onProceed={() => {
									setIsConfirmingRestart(false);
									// The host begins the descent again (a fresh seed, the save overwritten) and the player goes straight in.
									store.dispatch(restartSession()).then(onBegin, () => undefined);
								}}
								proceedLabel={titleScreen.restart.proceedLabel}
								sentence={walked(titleScreen.restart.confirm)}
							/>
						) : undefined}
						{/* D117 (Mike): the mute, on the landing, the moment there is
							anything to mute — the same preference the session toggle
							drives, so the choice follows the player through BEGIN.
							D204: and only when there IS anything to mute — a pack whose
							audio table is empty (the island's four) gets no switch for a
							sound it does not have (Mike, 2026-09-25); the session's gear
							has asked the same question since R35. */}
						{game.hasAudio ? (
							<AmbientAudioToggleComponent
								isAudioEnabled={isAmbientAudioEnabled}
								onToggle={(isAudioEnabled) => {
									store.dispatch(ambientAudioPreferenceSet(isAudioEnabled));
									ambiencePlayer.setEnabled(isAudioEnabled);
								}}
							/>
						) : undefined}
						{/* D153: a pack that runs a clock says so HERE, before the
						    descent, with the switch beside the words — WCAG 2.2.1's
						    "adjustable before it is met", as a paragraph and a button. */}
						{clock?.disclosure === undefined ? undefined : (
							<div className="player-shell__clock-notice">
								<p className="player-shell__clock-disclosure">{clock.disclosure}</p>
								<ClockToggleComponent
									isClockEnabled={isClockEnabled}
									onToggle={(isEnabled) => {
										store.dispatch(autoWaitPreferenceSet(isEnabled ? 'on' : 'off'));
									}}
								/>
							</div>
						)}
					</>
				) : (
					<button className="player-shell__begin" onClick={onWake} type="button">
						{titleScreen.wakeLabel}
					</button>
				)}
				<EngineCreditComponent />
			</div>
		</section>
	);
};
